import { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { RiArrowLeftLine } from '@remixicon/react'
import { cx, focusRing } from '../../lib/utils'

interface PageHeaderProps {
  title: string
  subtitle?: string
  /** Buttons aligned to the right of the title. */
  actions?: ReactNode
  /**
   * Where "back" goes, for a page reached from another one. Nested pages need
   * it: the sidebar only names top-level sections, so without it the way back
   * to a workspace is the browser's own button.
   */
  backTo?: { href: string; label: string }
}

export default function PageHeader({ title, subtitle, actions, backTo }: PageHeaderProps) {
  return (
    <header className="mb-8">
      {backTo && (
        <Link
          to={backTo.href}
          className={cx(
            'mb-3 inline-flex items-center gap-1.5 rounded text-sm text-gray-500 transition hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-50',
            focusRing,
          )}
        >
          <RiArrowLeftLine className="size-4" aria-hidden="true" />
          {backTo.label}
        </Link>
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          {/* The brand's loudest note: a heavy, tight, near-black title against
              a light subtitle. The contrast between the two is the voice. */}
          <h1 className="truncate font-display text-display text-gray-950 dark:text-gray-50">{title}</h1>
          {subtitle && (
            <p className="mt-1.5 max-w-2xl text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
          )}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    </header>
  )
}
